import { useState, useCallback, useEffect } from 'react'
import { X, TrendingDown, Target, Star, DollarSign, Check } from 'lucide-react'
import { getMatchTypeLabels } from '../utils/matchTypeLabels'

const SIDE_GAMES = [
  {
    betType: 'low_net',
    icon: TrendingDown,
    labelKey: 'lowNetEntireLabel',
    description: 'Cumulative low net across all rounds wins the pot',
  },
  {
    betType: 'low_gross',
    icon: Target,
    labelKey: 'lowGrossEntireLabel',
    description: 'Cumulative low gross (no handicap) across all rounds wins',
  },
  {
    betType: 'stableford',
    icon: Star,
    labelKey: 'stablefordEntireLabel',
    description: 'Cumulative Stableford points (with handicap) across all rounds. Highest wins.',
  },
  {
    betType: 'skins',
    icon: DollarSign,
    labelKey: 'skinsEntireLabel',
    description: 'Net skins across all rounds. Pot split proportionally by skins won.',
    isSkins: true,
  },
]

/** Find existing skins bet (could be 'skins' or 'skins_carry'). */
function findSkinsBet(bets) {
  return bets.find((b) => b.bet_type === 'skins' || b.bet_type === 'skins_carry')
}

export default function SideGameConfig({ matchId, match, sideBets, onCreateSideBet, onDeleteSideBet, isCreating, onClose }) {
  const labels = getMatchTypeLabels(match?.match_type)
  const isCompleted = match?.status === 'completed'
  const matchLevelBets = (sideBets || []).filter((b) => !b.game_id && b.scope === 'match')

  const existingSkins = findSkinsBet(matchLevelBets)

  const [amounts, setAmounts] = useState(() => {
    const initial = {}
    for (const sg of SIDE_GAMES) {
      if (sg.isSkins) {
        initial[sg.betType] = existingSkins ? String(Number(existingSkins.amount)) : '10'
      } else {
        const existing = matchLevelBets.find((b) => b.bet_type === sg.betType)
        initial[sg.betType] = existing ? String(Number(existing.amount)) : '10'
      }
    }
    return initial
  })

  const [skinsCarryOver, setSkinsCarryOver] = useState(existingSkins?.config?.carry_over ?? false)
  const [skinsGrossTiebreaker, setSkinsGrossTiebreaker] = useState(existingSkins?.config?.gross_tiebreaker ?? false)

  const [pendingBetType, setPendingBetType] = useState(null)

  useEffect(() => {
    if (!isCreating) setPendingBetType(null)
  }, [isCreating])

  const handleToggle = useCallback((betType) => {
    setPendingBetType(betType)
    if (betType === 'skins') {
      const existing = findSkinsBet(matchLevelBets)
      if (existing) {
        onDeleteSideBet(existing.id)
      } else {
        onCreateSideBet({
          match_id: matchId,
          bet_type: skinsCarryOver ? 'skins_carry' : 'skins',
          amount: parseFloat(amounts.skins) || 10,
          scope: 'match',
          config: { carry_over: skinsCarryOver, gross_tiebreaker: skinsGrossTiebreaker },
        })
      }
    } else {
      const existing = matchLevelBets.find((b) => b.bet_type === betType)
      if (existing) {
        onDeleteSideBet(existing.id)
      } else {
        onCreateSideBet({
          match_id: matchId,
          bet_type: betType,
          amount: parseFloat(amounts[betType]) || 10,
          scope: 'match',
        })
      }
    }
  }, [matchLevelBets, onDeleteSideBet, onCreateSideBet, matchId, amounts, skinsCarryOver, skinsGrossTiebreaker])

  // When skins config sub-options change while active, delete and re-create
  const handleSkinsConfigChange = useCallback((newCarryOver, newGrossTiebreaker) => {
    const existing = findSkinsBet(matchLevelBets)
    if (!existing) return
    setPendingBetType('skins')
    onDeleteSideBet(existing.id)
    // Small delay to ensure delete completes before create
    setTimeout(() => {
      onCreateSideBet({
        match_id: matchId,
        bet_type: newCarryOver ? 'skins_carry' : 'skins',
        amount: Number(existing.amount),
        scope: 'match',
        config: { carry_over: newCarryOver, gross_tiebreaker: newGrossTiebreaker },
      })
    }, 100)
  }, [matchLevelBets, onDeleteSideBet, onCreateSideBet, matchId])

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      backgroundColor: 'var(--bg-primary)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `calc(1rem + env(safe-area-inset-top, 0px)) 1rem 1rem 1rem`,
        borderBottom: '1px solid var(--border-color)',
        backgroundColor: 'var(--bg-primary)',
      }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: '700' }}>
          {labels.sideBetsLabel}
        </h2>
        <button
          onClick={onClose}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: 'var(--bg-tertiary)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--text-primary)',
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}>
        {SIDE_GAMES.map((sg) => {
          const Icon = sg.icon
          const existing = sg.isSkins ? findSkinsBet(matchLevelBets) : matchLevelBets.find((b) => b.bet_type === sg.betType)
          const isActive = !!existing
          const isBusy = isCreating && pendingBetType === sg.betType

          return (
            <div
              key={sg.betType}
              style={{
                padding: '1rem',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: '0.75rem',
                border: isActive ? '1px solid var(--accent-primary)' : '1px solid var(--border-color)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isActive ? '0.25rem' : '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flex: 1 }}>
                  <Icon size={18} style={{ color: isActive ? 'var(--accent-primary)' : 'var(--text-muted)', flexShrink: 0, marginTop: '0.125rem' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', fontSize: '0.9375rem' }}>{labels[sg.labelKey]}</div>
                    <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>{sg.description}</div>
                  </div>
                </div>
                {!isCompleted && (
                  <button
                    onClick={() => handleToggle(sg.betType)}
                    disabled={isBusy}
                    aria-label={isActive ? 'Disable' : 'Enable'}
                    style={{
                      position: 'relative',
                      width: '44px',
                      height: '24px',
                      borderRadius: '12px',
                      backgroundColor: isActive ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                      border: isActive ? 'none' : '1px solid var(--border-color)',
                      cursor: isBusy ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                      marginLeft: '0.5rem',
                      padding: 0,
                      transition: 'background-color 0.2s ease',
                      opacity: isBusy ? 0.5 : 1,
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: isActive ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      transition: 'left 0.2s ease',
                    }} />
                  </button>
                )}
              </div>

              {/* Amount input — only when not active and not completed */}
              {!isActive && !isCompleted && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                  <input
                    type="number"
                    min="1"
                    value={amounts[sg.betType]}
                    onChange={(e) => setAmounts((prev) => ({ ...prev, [sg.betType]: e.target.value }))}
                    style={{
                      width: '64px',
                      height: '32px',
                      textAlign: 'center',
                      borderRadius: '0.375rem',
                      fontSize: '0.8125rem',
                      fontWeight: '600',
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>per player</span>
                </div>
              )}

              {/* Active indicator */}
              {isActive && (
                <div style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', fontWeight: '600', marginTop: '0.25rem' }}>
                  Active — ${Number(existing.amount)} per player
                </div>
              )}

              {/* Skins sub-options: carry-over and gross tiebreaker */}
              {sg.isSkins && (isActive || (!isActive && !isCompleted)) && (
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      const newVal = !skinsCarryOver
                      setSkinsCarryOver(newVal)
                      if (isActive) handleSkinsConfigChange(newVal, skinsGrossTiebreaker)
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isCompleted ? 'default' : 'pointer', paddingLeft: '1.75rem' }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '0.1875rem',
                      border: `2px solid ${skinsCarryOver ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                      backgroundColor: skinsCarryOver ? 'var(--accent-primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      opacity: isCompleted ? 0.5 : 1,
                    }}>
                      {skinsCarryOver && <Check size={10} style={{ color: '#fff' }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Allow Carry-Overs</span>
                      <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Tied holes roll skins to the next hole</div>
                    </div>
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      const newVal = !skinsGrossTiebreaker
                      setSkinsGrossTiebreaker(newVal)
                      if (isActive) handleSkinsConfigChange(skinsCarryOver, newVal)
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isCompleted ? 'default' : 'pointer', paddingLeft: '1.75rem' }}
                  >
                    <div style={{
                      width: '16px', height: '16px', borderRadius: '0.1875rem',
                      border: `2px solid ${skinsGrossTiebreaker ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                      backgroundColor: skinsGrossTiebreaker ? 'var(--accent-primary)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      opacity: isCompleted ? 0.5 : 1,
                    }}>
                      {skinsGrossTiebreaker && <Check size={10} style={{ color: '#fff' }} />}
                    </div>
                    <div>
                      <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Low Gross Wins</span>
                      <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Tied net scores broken by lower gross</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Done button */}
      <div style={{
        padding: `0.75rem 1rem max(0.75rem, calc(0.25rem + env(safe-area-inset-bottom, 0px)))`,
        borderTop: '1px solid var(--border-color)',
        backgroundColor: 'var(--bg-primary)',
      }}>
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '0.875rem',
            borderRadius: '9999px',
            backgroundColor: 'var(--accent-primary)',
            color: '#fff',
            fontWeight: '700',
            fontSize: '1rem',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Done
        </button>
      </div>
    </div>
  )
}
